/**
 * MulchVolumeCalc — the most-used calculator in the trade.
 *
 * One shape (L × W × depth → cu yd) serves mulch, topsoil, gravel, sand,
 * compost. Per the research pain point #1, the coverage line shows at the
 * USER-ENTERED depth, not a 3-inch default. Default waste 10%.
 */

import React, { useMemo, useState } from 'react';
import { useIdioma } from '../../../../i18n/idioma';
import toast from 'react-hot-toast';
import { CalculatorProps } from '../registry';
import { priceLine } from '../../../../utils/lineItemPricing';
import { NumberField, SelectField, OutputCard, OutputRow, AddToEstimateButton, SectionHeader } from '../CalculatorPrimitives';
import type { EstimateItem, EstimateItemType } from '../../../../types';

type MaterialKey =
  | 'organic-mulch'
  | 'dyed-mulch'
  | 'topsoil'
  | 'compost'
  | 'aggregate-base'
  | 'drainage-gravel'
  | 'bedding-sand';

interface Material {
  key: MaterialKey;
  name: string;
  catalogId: string;
  /** Bulk unit suppliers price in. Most are cu yd; aggregates by ton. */
  bulkUnit: 'cu yd' | 'ton';
  /** Tons per cubic yard for weight-billed materials. ~1.4 for most aggregates. */
  tonsPerCuYd?: number;
  /** Whether 2-cu-ft and 3-cu-ft bag math makes sense (mulch yes, gravel no). */
  showBags: boolean;
}

const MATERIALS: Material[] = [
  { key: 'organic-mulch',   name: 'Organic mulch',         catalogId: 'landscaping.organic-mulch-cu-yd',     bulkUnit: 'cu yd', showBags: true  },
  { key: 'dyed-mulch',      name: 'Dyed mulch',            catalogId: 'landscaping.dyed-mulch-cu-yd',        bulkUnit: 'cu yd', showBags: true  },
  { key: 'topsoil',         name: 'Screened topsoil',      catalogId: 'landscaping.topsoil-screened-cuyd',   bulkUnit: 'cu yd', showBags: false },
  { key: 'compost',         name: 'Compost',               catalogId: 'landscaping.compost-cu-yd',           bulkUnit: 'cu yd', showBags: true  },
  { key: 'aggregate-base',  name: 'Aggregate base (3/4")', catalogId: 'landscaping.aggregate-base-3-4-ton',  bulkUnit: 'ton', tonsPerCuYd: 1.4, showBags: false },
  { key: 'drainage-gravel', name: 'Drainage gravel',       catalogId: 'landscaping.drainage-gravel-cuyd',    bulkUnit: 'cu yd', showBags: false },
  { key: 'bedding-sand',    name: 'Bedding sand',          catalogId: 'landscaping.bedding-sand-ton',        bulkUnit: 'ton', tonsPerCuYd: 1.35, showBags: false },
];

const MATERIAL_BY_KEY: Record<MaterialKey, Material> = Object.fromEntries(MATERIALS.map(m => [m.key, m])) as Record<MaterialKey, Material>;

const WASTE_PCT = 10;
const CU_FT_PER_CU_YD = 27;

const MulchVolumeCalc: React.FC<CalculatorProps> = ({ onAddTemplateItems, pricebookByHint, targetMarginPercent, materialMarkupPercent }) => {
    const { t } = useIdioma();
  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [depthIn, setDepthIn] = useState(3);
  const [materialKey, setMaterialKey] = useState<MaterialKey>('organic-mulch');

  const material = MATERIAL_BY_KEY[materialKey];

  const calc = useMemo(() => {
    const sqFt = (length || 0) * (width || 0);
    const cuFt = sqFt * ((depthIn || 0) / 12);
    const cuYdRaw = cuFt / CU_FT_PER_CU_YD;
    const cuYd = cuYdRaw * (1 + WASTE_PCT / 100);
    const tons = material.tonsPerCuYd ? cuYd * material.tonsPerCuYd : null;
    const bags2cf = cuFt > 0 ? Math.ceil((cuFt * (1 + WASTE_PCT / 100)) / 2) : 0;
    const bags3cf = cuFt > 0 ? Math.ceil((cuFt * (1 + WASTE_PCT / 100)) / 3) : 0;
    return { sqFt, cuFt, cuYdRaw, cuYd, tons, bags2cf, bags3cf };
  }, [length, width, depthIn, material]);

  const canAdd = calc.cuYd > 0;

  const handleAdd = () => {
    // Quantity follows the bulk unit the supplier bills in (cu yd or ton).
    const qty = material.bulkUnit === 'ton' && calc.tons ? calc.tons : calc.cuYd;
    const orderedQty = Math.ceil(qty * 4) / 4; // round up to nearest 0.25 to match supplier ordering.
    const cost = pricebookByHint.get(material.catalogId) ?? 0;
    // Mantillo y grava son MATERIAL: dial de MARKUP (sobre el COSTO), no de
    // margen (sobre el precio).
    const { rate, total, markupPercent, marginPercent } = priceLine('Material', cost, orderedQty, {
      targetMarginPercent,
      materialMarkupPercent,
    });

    const item: EstimateItem = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'Material' as EstimateItemType,
      description: `${material.name} — ${length}' × ${width}' × ${depthIn}" deep`,
      quantity: orderedQty,
      unit: material.bulkUnit,
      cost,
      rate,
      // El precio de la regla ES el precio base (k = 1). Sin estamparlo, una
      // fila agregada después de mover el dial de margen neto se quedaría una
      // generación atrás de otra idéntica agregada antes (Parte A.6.6).
      baseRate: rate,
      markupPercent,
      marginPercent,
      total,
      sourcePricebookId: material.catalogId,
      calcBasis: `${calc.sqFt.toFixed(0)} sqft × ${depthIn}" depth, +${WASTE_PCT}% waste`,
      isOverridden: false,
    };
    onAddTemplateItems([item], { id: 'calc.mulch-volume', name: `${material.name} — ${length}'×${width}'` });
    toast.success(`Added ${orderedQty} ${material.bulkUnit} of ${material.name.toLowerCase()}`);
  };

  return (
    <>
      <SectionHeader title={t('calc.medidasDelArriate')} />
      <div className="grid grid-cols-3 gap-3">
        <NumberField label="Length" unit="ft" value={length} onChange={setLength} />
        <NumberField label="Width" unit="ft" value={width} onChange={setWidth} />
        <NumberField label="Depth" unit="in" value={depthIn} onChange={setDepthIn} />
      </div>

      <SelectField
        label="Material"
        value={materialKey}
        onChange={(v) => setMaterialKey(v)}
        options={MATERIALS.map(m => ({ value: m.key, label: m.name }))}
      />

      <SectionHeader title={t('calc.loQueNecesitas')} />
      <OutputCard>
        <OutputRow
          label={material.bulkUnit === 'ton' ? 'Order (tons)' : 'Order (cubic yards)'}
          value={material.bulkUnit === 'ton' && calc.tons
            ? `${calc.tons.toFixed(2)} ton`
            : `${calc.cuYd.toFixed(2)} cu yd`}
          emphasis="primary"
          hint={`Includes ${WASTE_PCT}% waste`}
        />
        {material.bulkUnit === 'cu yd' && calc.cuYd > 0 && (
          <OutputRow label="Without waste" value={`${calc.cuYdRaw.toFixed(2)} cu yd`} emphasis="muted" />
        )}
        {material.showBags && calc.bags2cf > 0 && (
          <>
            <OutputRow label="2-cu-ft bags" value={`${calc.bags2cf}`} />
            <OutputRow label="3-cu-ft bags" value={`${calc.bags3cf}`} />
          </>
        )}
        {calc.sqFt > 0 && (
          <OutputRow
            label="Coverage"
            value={`${calc.sqFt.toFixed(0)} sq ft`}
            emphasis="muted"
            hint={`At ${depthIn}" depth`}
          />
        )}
      </OutputCard>

      <AddToEstimateButton
        label={canAdd
          ? `Add ${material.bulkUnit === 'ton' && calc.tons ? `${calc.tons.toFixed(2)} ton` : `${calc.cuYd.toFixed(2)} cu yd`} to estimate`
          : 'Enter dimensions to add'}
        onClick={handleAdd}
        disabled={!canAdd}
      />
    </>
  );
};

export default MulchVolumeCalc;
